import React from 'react'
import { Epic, ofType } from 'redux-observable'
import { AnyAction } from 'redux'
import { delay, filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { events } from './'
import { portfolioApiEvents } from '../../api/portfolio'
import { appRefreshApiEvents } from '../../appRefresh'
import { agencyApiEvents } from '../../api/agency'
import Button from '../../../views/components/atoms/Button/Button'
import { notificationSelectors } from '..'
import { findIndex, pathOr } from 'ramda'
import { restfulErrorEvent } from '../../../utils/restful'
import { reconApiEvents } from '../../api/recon'
import { apiStateSelectors } from '../../api/apiState'
import { walletApiEvents } from '../../api/wallet'
import { invoiceApiEvents } from '../../api/invoice'
import { bankingApiEvents } from '../../api/banking'
import { communicationsApiEvents } from '../../communication/state'
import { StatusCodes } from 'http-status-codes'
import { userApiEvents } from '../../api/user'
import { $TSFixMe } from 'types/ts-migrate'
import { reportingApiEvents } from 'modules/api/reporting'
import { partyApiEvents, partyApiSelectors } from 'modules/api/party'
import { integrationsApiEvents } from 'modules/api/integrations'
import { propertyApiEvents } from 'modules/api/property'
import { tpnApiConstants, tpnApiEvents } from 'modules/api/tpn'
import { formatDate } from 'utils/date'
import { EpicDependencies, RootState } from 'src/state/types'

type NotificationEpic = Epic<AnyAction, AnyAction, RootState, EpicDependencies>

const notificationEpicFactory =
  ({ name, action, type, heading, message, autoDismiss, children }: any): NotificationEpic =>
  action$ =>
    action$.pipe(
      ofType(action),
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
      map(() => events.addNotification({ type, heading, message, children }, { autoDismiss })),
      tag(`module/epics/${name}`),
    )

const filterErrorInitAction = (initAction: { toString: () => string }) => (event: AnyAction) =>
  event?.meta?.initAction.type === initAction.toString()

const notificationErrorEpicFactory =
  ({
    name,
    initAction,
    heading,
    fallbackMessage = 'Something went wrong. Please try again or contact support.',
    children,
    extraFilter,
  }: {
    name: string
    initAction: { toString: () => string }
    heading: string
    fallbackMessage?: string
    children?: React.ReactNode
    extraFilter?: (event: AnyAction) => boolean
  }): NotificationEpic =>
  action$ =>
    action$.pipe(
      ofType(restfulErrorEvent),
      filter(event => filterErrorInitAction(initAction)(event) && (extraFilter ? extraFilter(event) : true)),
      map(event =>
        events.addNotification(
          {
            type: 'error',
            heading,
            children: children ?? (
              <p>{pathOr(fallbackMessage, ['payload', 'response', 'errors', 0, 'message'], event)}</p>
            ),
          },
          // @ts-expect-error ts-migrate(2554)
          { autoDismiss: false },
        ),
      ),
      tag(`module/epics/${name}`),
    )

export const autoDismissNotification: NotificationEpic = (action$, state$) =>
  action$.pipe(
    ofType(events.addNotification),
    pluck('meta'),
    filter(meta => meta?.autoDismiss),
    delay(5000),
    map(() => {
      const notifications = notificationSelectors.getNotifications(state$.value)
      const index = findIndex((n: any) => n.autoDismiss, notifications)

      return events.clearNotification(index)
    }),
    tag('module/epics/autoDismissNotification'),
  )

/**
 * App refresh notifications
 */
export const versionMismatched = notificationEpicFactory({
  name: 'versionMismatched',
  action: appRefreshApiEvents.versionMismatched,
  type: 'info',
  heading: 'An update is available!',
  message: 'A new version of reOS has been released, click Update below to ensure you are up to date.',
  autoDismiss: false,
  children: (
    <Button
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; primary: true; size: str... Remove this comment to see the full error message
      primary
      size="sm"
      onClick={() => window.location.reload()}
    >
      Update now!
    </Button>
  ),
})

/**
 * Portfolio notifications
 */
export const approveSuccess = notificationEpicFactory({
  name: 'approveSuccess',
  action: portfolioApiEvents.approve_success,
  type: 'success',
  heading: 'Activation successful',
})
export const renewalSuccess = notificationEpicFactory({
  name: 'renewalSuccess',
  action: portfolioApiEvents.renewal_success,
  type: 'success',
  heading: 'Renewal successful',
})
export const cancelRenewal = notificationEpicFactory({
  name: 'cancelRenewal',
  action: portfolioApiEvents.cancelRenewal_success,
  type: 'success',
  heading: 'Renewal cancelled',
})
export const terminateApprovedLeaseSuccess = notificationEpicFactory({
  name: 'terminateApprovedLeaseSuccess',
  action: portfolioApiEvents.terminateApprovedLease_success,
  type: 'success',
  heading: 'Termination successful',
})
export const deleteDraftLeaseSuccess = notificationEpicFactory({
  name: 'deleteDraftLeaseSuccess',
  action: portfolioApiEvents.deleteDraftLease_success,
  type: 'success',
  heading: 'Draft lease deleted',
})
export const acceptApplicationSuccess = notificationEpicFactory({
  name: 'acceptApplicationSuccess',
  action: portfolioApiEvents.acceptApplication_success,
  type: 'success',
  heading: 'Application accepted',
})
export const addInvoiceTemplateFailed = notificationErrorEpicFactory({
  name: 'addInvoiceTemplateFailed',
  initAction: portfolioApiEvents.addInvoiceTemplate_request,
  heading: 'Invoice template creation failed',
  children: <p>There was a conflict saving invoice templates, please reload and try again</p>,
  extraFilter: (event: any) => event?.payload?.status === StatusCodes.CONFLICT,
})
export const approveFailed = notificationErrorEpicFactory({
  name: 'approveFailed',
  initAction: portfolioApiEvents.approve_request,
  heading: 'Activation failed',
})
export const amendContractFailed = notificationErrorEpicFactory({
  name: 'amendContractFailed',
  initAction: portfolioApiEvents.amendRentAndFees_request,
  heading: 'Failed to save',
})
export const terminateApprovedLeaseFailed = notificationErrorEpicFactory({
  name: 'terminateApprovedLeaseFailed',
  initAction: portfolioApiEvents.terminateApprovedLease_request,
  heading: 'Termination failed',
})
export const deleteDraftLeaseFailed = notificationErrorEpicFactory({
  name: 'deleteDraftLeaseFailed',
  initAction: portfolioApiEvents.deleteDraftLease_request,
  heading: 'Failed to delete draft lease',
})
export const acceptApplicationFailed = notificationErrorEpicFactory({
  name: 'acceptApplicationFailed',
  initAction: portfolioApiEvents.acceptApplication_request,
  heading: 'Failed to accept application',
})

/**
 * Agency notifications
 */
export const partyInviteSuccess = notificationEpicFactory({
  name: 'partyInviteSuccess',
  action: agencyApiEvents.sendPartyInvite_success,
  type: 'success',
  heading: 'Invitation has been sent',
})
export const inviteSuccess = notificationEpicFactory({
  name: 'inviteSuccess',
  action: agencyApiEvents.invite_success,
  type: 'success',
  heading: 'Invitation has been sent',
})
export const existingInvite = notificationEpicFactory({
  name: 'existingInvite',
  action: agencyApiEvents.existingInviteNotification,
  type: 'error',
  heading: 'Invitation not sent',
  message: 'An invitation has already been sent to someone with this email address.',
})
export const failedInvite = notificationEpicFactory({
  name: 'failedInvite',
  action: agencyApiEvents.failedInviteNotification,
  type: 'error',
  heading: 'Invitation failed to sent',
})
export const addToGroupSuccess = notificationEpicFactory({
  name: 'addToGroupSuccess',
  action: agencyApiEvents.addToGroup_success,
  type: 'success',
  heading: 'Agency added to group',
  message: 'Reload is required to see updated list of agencies and groups.',
  autoDismiss: false,
  children: (
    <Button
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; primary: true; size: str... Remove this comment to see the full error message
      primary
      size="sm"
      onClick={() => window.location.reload()}
    >
      Reload
    </Button>
  ),
})
export const removeFromGroupSuccess = notificationEpicFactory({
  name: 'removeFromGroupSuccess',
  action: agencyApiEvents.removeFromGroup_success,
  type: 'success',
  heading: 'Agency removed from group',
  message: 'Reload is required to see updated list of agencies and groups.',
  autoDismiss: false,
  children: (
    <Button
      // @ts-expect-error ts-migrate(2322) FIXME: Type '{ children: string; primary: true; size: str... Remove this comment to see the full error message
      primary
      size="sm"
      onClick={() => window.location.reload()}
    >
      Reload
    </Button>
  ),
})
export const deactivateAgencySuccess = notificationEpicFactory({
  name: 'deactivateAgencySuccess',
  action: agencyApiEvents.deactivateAgency_success,
  type: 'success',
  heading: 'Agency deactivated',
})
export const deactivateAgencyFailed = notificationErrorEpicFactory({
  name: 'deactivateAgencyFailed',
  initAction: agencyApiEvents.deactivateAgency_request,
  heading: 'Failed to deactivate agency',
})
export const activateAgencySuccess = notificationEpicFactory({
  name: 'activateAgencySuccess',
  action: agencyApiEvents.activateAgency_success,
  type: 'success',
  heading: 'Agency activated',
})
export const activateAgencyFailed = notificationErrorEpicFactory({
  name: 'activateAgencyFailed',
  initAction: agencyApiEvents.activateAgency_request,
  heading: 'Failed to activate agency',
})

/**
 * Recon notifications
 */
export const failedBeneficiaryUpdate: NotificationEpic = (action$, state$) =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(reconApiEvents.updateBenficiaries_request)),
    map(event => {
      const payloadKey = event?.meta?.initAction?.payload
      const generalErrors = apiStateSelectors.getGeneralFormErrorsByEvent(state$.value)(
        reconApiEvents.updateBenficiaries_request(payloadKey),
      )
      return events.addNotification(
        {
          type: 'error',
          heading: 'Beneficaries update failed',
          children: (
            <>
              {generalErrors.map((e: any, i: any) => (
                <p key={i}>{e}</p>
              ))}
            </>
          ),
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: false },
      )
    }),
    tag('module/epics/failedBeneficiaryUpdate'),
  )
export const failedApprovePayment: NotificationEpic = (action$, state$) =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(reconApiEvents.payment_request)),
    map(event => {
      const payloadKey = event?.meta?.initAction?.payload
      const generalErrors = apiStateSelectors.getGeneralFormErrorsByEvent(state$.value)(
        reconApiEvents.payment_request(payloadKey),
      )
      return events.addNotification(
        {
          type: 'error',
          heading: 'Payment approval failed',
          children: (
            <>
              {generalErrors.map((e: any, i: any) => (
                <p key={`fap-${i}`}>{e}</p>
              ))}
            </>
          ),
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: false },
      )
    }),
    tag('module/epics/failedApprovePayment'),
  )
export const failedCreditNote: NotificationEpic = (action$, state$) =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(reconApiEvents.creditNote_request)),
    map(() => {
      const generalErrors = apiStateSelectors.getGeneralFormErrorsByEvent(state$.value)(
        reconApiEvents.creditNote_request,
      )
      return events.addNotification(
        {
          type: 'error',
          heading: 'Credit note failed',
          children: (
            <>
              {generalErrors.map((e: any, i: any) => (
                <p key={`fcn-${i}`}>{e}</p>
              ))}
            </>
          ),
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: false },
      )
    }),
    tag('module/epics/failedCreditNote'),
  )
export const invoiceNudgeSuccess: NotificationEpic = (action$, state$) =>
  action$.pipe(
    ofType(reconApiEvents.invoicesNudge_success),
    map(event => {
      const payloadKey = event.payload.nudgeResult
      if (payloadKey && payloadKey.length === 1) {
        return events.addNotification(
          {
            type: 'success',
            heading: 'Invoice nudged',
            message: '',
          },
          // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
          { autoDismiss: true },
        )
      } else {
        const notifications = notificationSelectors.getNotifications(state$.value)
        const index = findIndex((n: any) => n.autoDismiss, notifications)

        return events.clearNotification(index)
      }
    }),
    tag('module/epics/invoiceNudgeSuccess'),
  )
export const invoiceNudgeFailed: NotificationEpic = (action$, state$) =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(reconApiEvents.invoicesNudge_request)),
    map(event => {
      const payloadKey = event.meta.initAction.payload
      if (payloadKey && payloadKey.invoiceIds.length === 1) {
        return events.addNotification(
          {
            type: 'error',
            heading: 'Invoice nudge failed',
            children: <p>You can only nudge an invoice every 24 hours.</p>,
          },
          // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
          { autoDismiss: false },
        )
      } else {
        const notifications = notificationSelectors.getNotifications(state$.value)
        const index = findIndex((n: any) => n.autoDismiss, notifications)

        return events.clearNotification(index)
      }
    }),
    tag('module/epics/invoiceNudgeFailed'),
  )

/**
 * Invoices
 */
export const invoiceCreatedSuccess = notificationEpicFactory({
  name: 'invoiceCreatedSuccess',
  action: invoiceApiEvents.createOnceOffInvoice_success,
  type: 'success',
  heading: 'Invoice created',
})
export const failedSendInvoice = notificationErrorEpicFactory({
  name: 'failedSendInvoice',
  initAction: invoiceApiEvents.sendInvoices_request,
  heading: 'Send invoice failed',
  children: <p>Something went wrong, please reload and try again.</p>,
})
export const failedDeleteInvoice = notificationErrorEpicFactory({
  name: 'failedDeleteInvoice',
  initAction: invoiceApiEvents.deleteInvoices_request,
  heading: 'Delete invoice failed',
  children: <p>Something went wrong, please reload and try again.</p>,
})

/**
 * Payments
 */
export const reversePaymentsFailed = notificationErrorEpicFactory({
  name: 'reversePaymentsFailed',
  initAction: walletApiEvents.reversePayments_request,
  heading: 'Error reversing payments',
  children: <p>Something went wrong, please reload and try again.</p>,
})
export const demandPaymentsFailed = notificationErrorEpicFactory({
  name: 'demandPaymentsFailed',
  initAction: walletApiEvents.demandPayments_request,
  heading: 'Error sending payments',
  children: <p>Something went wrong, please reload and try again.</p>,
})
export const saveBulkInvoices = notificationErrorEpicFactory({
  name: 'saveBulkInvoices',
  initAction: invoiceApiEvents.saveBulkInvoices_request,
  heading: 'Error saving bulk import data',
  children: <p>Something went wrong, please try again.</p>,
})
export const reversePaymentsSuccess: NotificationEpic = action$ =>
  action$.pipe(
    ofType(walletApiEvents.reversePayments_success),
    map(() =>
      events.addNotification(
        {
          type: 'success',
          heading: 'Payments reversed',
          message: '',
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: true },
      ),
    ),
    tag('module/epics/reversePaymentsSuccess'),
  )
export const demandPaymentsSuccess: NotificationEpic = action$ =>
  action$.pipe(
    ofType(walletApiEvents.demandPayments_success),
    map(() =>
      events.addNotification(
        {
          type: 'success',
          heading: 'Payments sent',
          message: '',
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: true },
      ),
    ),
    tag('module/epics/demandPaymentsSuccess'),
  )
export const approvePaymentsFailed = notificationErrorEpicFactory({
  name: 'approvePaymentsFailed',
  initAction: bankingApiEvents.partiesErrors_request,
  heading: 'Some payments skipped',
  children: <p>Skipped beneficiaries missing bank account details. Review your payment rules to add them and retry.</p>,
  // Only a 422 carries the parties the bank-details check wants skipped. Any other failure
  // (a 5xx, or a transport error, which reaches us as status 0) means the check never ran:
  // the approval falls back to paying every beneficiary, so nothing was skipped and this
  // message would send the user hunting for missing bank details that aren't the problem.
  // `restfulErrorEvent` puts the status on meta, never on payload.
  extraFilter: event => event?.meta?.status === StatusCodes.UNPROCESSABLE_ENTITY,
})
export const transferWalletFundsSuccess: NotificationEpic = action$ =>
  action$.pipe(
    ofType(walletApiEvents.walletTransfer_success),
    map(() =>
      events.addNotification(
        {
          type: 'success',
          heading: 'Transfer successful',
          message: '',
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: true },
      ),
    ),
    tag('module/epics/transferWalletFundsSuccess'),
  )
export const transferWalletFundsFailed = notificationErrorEpicFactory({
  name: 'transferWalletFundsFailed',
  initAction: walletApiEvents.walletTransfer_request,
  heading: 'Transfer failed',
  children: <p>Something went wrong, please try again.</p>,
})
export const refundAccountSuccess: NotificationEpic = action$ =>
  action$.pipe(
    ofType(walletApiEvents.refund_success),
    map(() =>
      events.addNotification(
        {
          type: 'success',
          heading: 'Refund successful',
          message: '',
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: true },
      ),
    ),
    tag('module/epics/refundAccountSuccess'),
  )
export const refundAccountFailed = notificationErrorEpicFactory({
  name: 'refundAccountFailed',
  initAction: walletApiEvents.refund_request,
  heading: 'Refund failed',
  children: <p>Something went wrong, please try again.</p>,
})

// Suspense Account notifications
export const suspenseAccountAllocationSuccess = notificationEpicFactory({
  name: 'suspenseAccountAllocationSuccess',
  action: walletApiEvents.allocateSuspenseAccount_success,
  type: 'success',
  heading: 'Allocation successful',
})

// Bulk Wallet Transfer notifications
export const bulkWalletTransferFailed = notificationErrorEpicFactory({
  name: 'bulkWalletTransferFailed',
  initAction: walletApiEvents.bulkWalletTransfer_request,
  heading: 'Bulk Wallet Transfer failed',
  children: <p>Something went wrong, please try again.</p>,
})
export const bulkWalletTransferSuccess = notificationEpicFactory({
  name: 'bulkWalletTransferSuccess',
  action: walletApiEvents.bulkWalletTransfer_success,
  type: 'success',
  heading: 'Bulk Wallet Transfer successful',
})

// Commission withdrawal via the Robin Drawer
export const commissionWalletWithdrawalSuccess: NotificationEpic = action$ =>
  action$.pipe(
    ofType(walletApiEvents.payout_success),
    map(() =>
      events.addNotification(
        {
          type: 'success',
          heading: 'Commission withdrawal successful',
          message: '',
        },
        // @ts-expect-error
        { autoDismiss: true },
      ),
    ),
    tag('module/epics/commissionWalletWithdrawalSuccess'),
  )
export const commissionWalletWithdrawalFailed = notificationErrorEpicFactory({
  name: 'commissionWalletWithdrawalFailed',
  initAction: walletApiEvents.payout_request,
  heading: 'Commission withdrawal failed',
  children: <p>Something went wrong, please try again.</p>,
})

export const walletWithdrawalSuccess = (action$: any, state$: any) =>
  action$.pipe(
    ofType(walletApiEvents.withdraw_success),
    map(event => {
      return events.addNotification(
        {
          type: 'success',
          heading: 'Withdrawal successful',
          message: '',
        },
        // @ts-expect-error
        { autoDismiss: true },
      )
    }),
    tag('module/epics/walletWithdrawalSuccess'),
  )

export const walletWithdrawalFailed = (action$: any, state$: any) =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(walletApiEvents.withdraw_request)),
    map(event => {
      return events.addNotification(
        {
          type: 'error',
          heading: 'Withdrawal failed',
          children: <p>Something went wrong, please try again.</p>,
        },
        // @ts-expect-error
        { autoDismiss: false },
      )
    }),
    tag('module/epics/walletWithdrawalFailed'),
  )

export const consolidatedWalletWithdrawalSuccess = (action$: any, state$: any) =>
  action$.pipe(
    ofType(walletApiEvents.consolidatedWithdraw_success),
    map(event => {
      return events.addNotification(
        {
          type: 'success',
          heading: 'Withdrawal successful',
          message: '',
        },
        // @ts-expect-error
        { autoDismiss: true },
      )
    }),
    tag('module/epics/consolidatedWalletWithdrawalSuccess'),
  )

export const consolidatedWalletWithdrawalFailed = (action$: any, state$: any) =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(walletApiEvents.consolidatedWithdraw_request)),
    map(event => {
      return events.addNotification(
        {
          type: 'error',
          heading: 'Withdrawal failed',
          children: <p>Something went wrong, please try again.</p>,
        },
        // @ts-expect-error
        { autoDismiss: false },
      )
    }),
    tag('module/epics/consolidatedWalletWithdrawalFailed'),
  )

/**
 * Email notifications
 */

export const resendSuccess = notificationEpicFactory({
  name: 'resendSuccess',
  action: communicationsApiEvents.resend_success,
  type: 'success',
  heading: 'Email successfully resent',
})
export const resentFailed = notificationErrorEpicFactory({
  name: 'resentFailed',
  initAction: communicationsApiEvents.resend_request,
  heading: 'Resending Email Failed',
  fallbackMessage: 'Please try resending the email again.',
})

/**
 * User notifications
 */
export const resendVerificationEmailSuccess = notificationEpicFactory({
  name: 'resendVerificationEmailSuccess',
  action: userApiEvents.resendEmailVerification_success,
  type: 'success',
  heading: 'Email verification sent',
})
export const revokeAccessSuccess = notificationEpicFactory({
  name: 'revokeAccessSuccess',
  action: userApiEvents.revokeAccess_success,
  type: 'success',
  heading: 'User access revoked',
})
export const changePermissionsSuccess = notificationEpicFactory({
  name: 'changePermissionsSuccess',
  action: userApiEvents.changePermissions_success,
  type: 'success',
  heading: 'User permissions updated',
})
export const adminRemoveMembershipSuccess = notificationEpicFactory({
  name: 'adminRemoveMembershipSuccess',
  action: userApiEvents.adminRemoveMembership_success,
  type: 'success',
  heading: 'Agency access removed',
})
export const adminRemoveMembershipFailed = notificationErrorEpicFactory({
  name: 'adminRemoveMembershipFailed',
  initAction: userApiEvents.adminRemoveMembership_request,
  heading: 'Failed to remove agency access',
})
export const adminGrantAccessFailed = notificationErrorEpicFactory({
  name: 'adminGrantAccessFailed',
  initAction: userApiEvents.adminGrantAccess_request,
  heading: 'Failed to grant agency access',
})
export const adminLookupUserFailed = notificationErrorEpicFactory({
  name: 'adminLookupUserFailed',
  initAction: userApiEvents.adminLookupUser_request,
  heading: 'Failed to look up user',
})
export const adminAgencyMembersFailed = notificationErrorEpicFactory({
  name: 'adminAgencyMembersFailed',
  initAction: userApiEvents.adminAgencyMembers_request,
  heading: 'Failed to load agency members',
})
export const revokeAccessFailed = notificationErrorEpicFactory({
  name: 'revokeAccessFailed',
  initAction: userApiEvents.revokeAccess_request,
  heading: 'Failed to revoke user access',
})
export const changePermissionsFailed = notificationErrorEpicFactory({
  name: 'changePermissionsFailed',
  initAction: userApiEvents.changePermissions_request,
  heading: 'Failed to update user permissions',
})
export const disableUserSuccess = notificationEpicFactory({
  name: 'disableUserSuccess',
  action: userApiEvents.disableUser_success,
  type: 'success',
  heading: 'User disabled',
})
export const disableUserFailed = notificationErrorEpicFactory({
  name: 'disableUserFailed',
  initAction: userApiEvents.disableUser_request,
  heading: 'Failed to disable user',
})
export const enableUserFailed = notificationErrorEpicFactory({
  name: 'enableUserFailed',
  initAction: userApiEvents.enableUser_request,
  heading: 'Failed to enable user',
})

/**
 * Party notifications
 */
export const createPartySuccess = notificationEpicFactory({
  name: 'createPartySuccess',
  action: partyApiEvents.createParty_success,
  type: 'success',
  heading: 'Contact created',
})
export const updatePartySuccess = notificationEpicFactory({
  name: 'updatePartySuccess',
  action: partyApiEvents.updateParty_success,
  type: 'success',
  heading: 'Contact updated',
})
export const createPartyFailed = notificationErrorEpicFactory({
  name: 'createPartyFailed',
  initAction: partyApiEvents.createParty_request,
  heading: 'Failed to create contact',
})
export const updatePartyFailed = notificationErrorEpicFactory({
  name: 'updatePartyFailed',
  initAction: partyApiEvents.updateParty_request,
  heading: 'Failed to update contact',
})

/**
 * Reporting notifications
 */
export const downloadPropertyTaxStatementFailed: NotificationEpic = action$ =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(reportingApiEvents.propertyTaxStatement_request)),
    map(event => {
      const ownerName = event.meta.initAction.payload.ownerName
      return events.addNotification(
        {
          type: 'error',
          heading: `No Taxable Income for ${ownerName} for the selected date range.`,
          message: '',
        },
        // @ts-expect-error
        { autoDismiss: false },
      )
    }),
    tag('module/epics/downloadPropertyTaxStatementFailed'),
  )

export const downloadOwnerStatementFailed: NotificationEpic = action$ =>
  action$.pipe(
    ofType(restfulErrorEvent),
    filter(filterErrorInitAction(reportingApiEvents.ownerStatement_request)),
    map(event => {
      const ownerName = event.meta.initAction.payload.ownerName
      return events.addNotification(
        {
          type: 'error',
          heading: `Unable to generate the Owner Statement for ${ownerName} for the selected date range.`,
          message: '',
        },
        // @ts-expect-error
        { autoDismiss: false },
      )
    }),
    tag('module/epics/downloadOwnerStatementFailed'),
  )

/**
 * Integration notifications
 */

export const refreshPropDataIntegrationFailed = notificationErrorEpicFactory({
  name: 'refreshPropDataIntegrationFailed',
  initAction: integrationsApiEvents.refreshPropDataIntegration_request,
  heading: 'Failed to refresh PropData integration',
  children: <p>Something went wrong. Please try again or contact support.</p>,
})
export const refreshPropDataIntegrationSuccess = notificationEpicFactory({
  name: 'refreshPropDataIntegrationSuccess',
  action: integrationsApiEvents.refreshPropDataIntegration_success,
  type: 'success',
  heading: 'PropData integration refreshed',
})

/**
 * Property notifications
 */
export const updatePropertySuccess = notificationEpicFactory({
  name: 'updatePropertySuccess',
  action: propertyApiEvents.updateProperty_success,
  type: 'success',
  heading: 'Property updated successfully',
})

/**
 * TPN report upload notifications
 *
 * le-go answers errors as `{ message }` (echo's HTTPError), not Phoenix's
 * `{ errors: [] }` shape, so the error epics read the message directly rather
 * than going through notificationErrorEpicFactory.
 */
const tpnErrorEpicFactory =
  ({
    name,
    initAction,
    heading,
    autoDismiss = false,
  }: {
    name: string
    initAction: { toString: () => string }
    heading: string
    autoDismiss?: boolean
  }): NotificationEpic =>
  action$ =>
    action$.pipe(
      ofType(restfulErrorEvent),
      filter(filterErrorInitAction(initAction)),
      mergeMap(event => {
        const notConfigured = event?.meta?.status === StatusCodes.PRECONDITION_FAILED
        const message = notConfigured
          ? 'Connect your TPN upload account first.'
          : pathOr(
              pathOr(
                'Something went wrong. Please try again or contact support.',
                ['payload', 'response', 'errors', 0, 'message'],
                event,
              ),
              ['payload', 'response', 'message'],
              event,
            )
        const notification = events.addNotification(
          { type: 'error', heading, children: <p>{message}</p> },
          // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
          { autoDismiss },
        )
        // 412 means the server has no settings while the client still shows
        // connected: re-read so the card and modal resync. Never for the
        // settings fetch itself, or a 412 on GET would loop forever.
        const resync = notConfigured && initAction.toString() !== tpnApiEvents.tpnSettings_request.toString()
        return resync ? [notification, tpnApiEvents.tpnSettings_request()] : [notification]
      }),
      tag(`module/epics/${name}`),
    )

// Fires on every /integrations mount while insights is down, so it must not stick.
export const tpnSettingsLoadFailed = tpnErrorEpicFactory({
  name: 'tpnSettingsLoadFailed',
  initAction: tpnApiEvents.tpnSettings_request,
  heading: 'Unable to load TPN upload settings',
  autoDismiss: true,
})

export const tpnConnected = notificationEpicFactory({
  name: 'tpnConnected',
  action: tpnApiEvents.saveTpnSettings_success,
  type: 'success',
  heading: 'TPN connected',
  message: 'Your TPN upload account is linked. You can send the report now or turn on automatic uploads.',
})

// No toast for a failed connect: `TpnConnectForm` shows `saveError` inline.

export const tpnDisconnected = notificationEpicFactory({
  name: 'tpnDisconnected',
  action: tpnApiEvents.removeTpnSettings_success,
  type: 'success',
  heading: 'TPN disconnected',
  message: 'Manual and automatic uploads have stopped.',
})

export const tpnDisconnectFailed = tpnErrorEpicFactory({
  name: 'tpnDisconnectFailed',
  initAction: tpnApiEvents.removeTpnSettings_request,
  heading: 'Unable to disconnect TPN',
})

export const tpnAutoSendUpdated: NotificationEpic = action$ =>
  action$.pipe(
    ofType(tpnApiEvents.setTpnAutoSend_success),
    map(event =>
      events.addNotification(
        {
          type: 'success',
          heading: event.payload.enabled ? 'Automatic TPN uploads on' : 'Automatic TPN uploads off',
          message: event.payload.enabled
            ? `The report will be uploaded on the ${tpnApiConstants.TPN_UPLOAD_SCHEDULE}.`
            : 'The report will only be uploaded when you send it.',
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: true },
      ),
    ),
    tag('module/epics/tpnAutoSendUpdated'),
  )

export const tpnAutoSendFailed = tpnErrorEpicFactory({
  name: 'tpnAutoSendFailed',
  initAction: tpnApiEvents.setTpnAutoSend_request,
  heading: 'Unable to update automatic TPN uploads',
})

export const tpnReportSent: NotificationEpic = action$ =>
  action$.pipe(
    ofType(tpnApiEvents.sendTpnReport_success),
    map(event =>
      events.addNotification(
        {
          type: 'success',
          heading: 'TPN report sent',
          children: (
            <p>
              {event.payload.fileName} ({formatDate(event.payload.windowStart)} to {formatDate(event.payload.windowEnd)}
              ) is in your TPN staging area for review.
            </p>
          ),
        },
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
        { autoDismiss: false },
      ),
    ),
    tag('module/epics/tpnReportSent'),
  )

export const tpnReportSendFailed = tpnErrorEpicFactory({
  name: 'tpnReportSendFailed',
  initAction: tpnApiEvents.sendTpnReport_request,
  heading: 'TPN report not sent',
})
